import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://pandabuy.wiki';
const LOGO_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg';

interface JsonLdProps {
  data: Record<string, unknown>;
}

export default function JsonLd({ data }: JsonLdProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [data]);

  return null;
}

export function buildWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Pandabuy Spreadsheet Wiki',
    url: SITE_URL,
    description: 'Discover verified Pandabuy Spreadsheet lists, curated product finds, and optimized shopping data designed to help you find the best items at the lowest cost.',
    inLanguage: 'en',
    publisher: {
      '@type': 'Organization',
      name: 'pandabuy.wiki',
      url: SITE_URL,
      logo: LOGO_URL,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/?s={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function buildWebPageSchema(title: string, description: string, path: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: `${SITE_URL}${path}`,
    inLanguage: 'en',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Pandabuy Spreadsheet Wiki',
      url: SITE_URL,
    },
    about: {
      '@type': 'Thing',
      name: 'Pandabuy Spreadsheet Database',
      description: 'A curated spreadsheet database for pandabuy product sourcing and shopping agent optimization',
    },
  };
}

export function buildArticleSchema(
  headline: string,
  description: string,
  image: string,
  datePublished: string,
  path: string,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    image,
    datePublished,
    dateModified: '2026-07-01',
    author: {
      '@type': 'Organization',
      name: 'pandabuy.wiki Editorial Team',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'pandabuy.wiki',
      url: SITE_URL,
      logo: LOGO_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}${path}`,
    },
    inLanguage: 'en',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Pandabuy Spreadsheet Wiki',
      url: SITE_URL,
    },
  };
}

export function buildBreadcrumbSchema(items: Array<{ name: string; path: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}